import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';
import { useCart } from '../context/CartContext';
import { useRegion } from '../context/RegionContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Section = styled.section`padding: 120px 0 160px; background:#F7F5F0;`;
const Container = styled.div`max-width:1400px; margin:0 auto; padding:0 32px;`;
const FilterBar = styled.div`display:flex; gap:10px; flex-wrap:wrap;`;
const FilterBtn = styled.button`
  padding:9px 24px; border-radius:999px; font-size:.78rem; letter-spacing:.1em; cursor:pointer;
  transition:background .22s, color .22s, border-color .22s, transform .18s;
  border:1px solid ${({$active})=>$active?'#7C3A00':'rgba(26,18,8,0.15)'};
  background:${({$active})=>$active?'#7C3A00':'transparent'};
  color:${({$active})=>$active?'#FFFDF7':'rgba(26,18,8,0.55)'};
  &:hover{ border-color:#7C3A00; transform:translateY(-1px); }
`;
const Grid = styled.div`display:grid; gap:20px; grid-template-columns: repeat(2,1fr); @media(min-width:900px){grid-template-columns:repeat(3,1fr);} @media(min-width:1280px){grid-template-columns:repeat(4,1fr);}`;
const Card = styled.div`background:#FFFDF7; border-radius:16px; overflow:hidden; border:1px solid rgba(26,18,8,0.08); transition: transform .25s, box-shadow .25s; cursor:pointer; &:hover{transform:translateY(-6px); box-shadow:0 14px 40px rgba(26,18,8,0.08);}`;
const ImageArea = styled.div`aspect-ratio:4/3; display:flex; align-items:center; justify-content:center; position:relative; background:#fff`;
const TypePill = styled.span`position:absolute; top:12px; left:12px; background:rgba(255,253,247,0.92); padding:6px 10px; border-radius:999px; font-size:.66rem; color:#7C3A00; border:1px solid rgba(124,58,0,0.12);`;
const QuickBtn = styled.button`position:absolute; bottom:12px; right:12px; width:40px; height:40px; border-radius:50%; background:#FFFDF7; border:1px solid rgba(26,18,8,0.06); display:flex; align-items:center; justify-content:center;`;
const CardBody = styled.div`padding:18px; display:flex; flex-direction:column; gap:8px;`;
const ProductName = styled.h3`font-family:'Cormorant Garamond', serif; margin:0;`;
const ProductDesc = styled.p`margin:0; color:rgba(26,18,8,0.6); font-size:.95rem;`;
const CardFooter = styled.div`display:flex; justify-content:space-between; align-items:center; margin-top:12px;`;

const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;
const SkeletonCard = styled.div`
  border-radius:16px; overflow:hidden; border:1px solid rgba(26,18,8,0.08);
  background: linear-gradient(90deg, #ede8e0 25%, #f5f0e8 50%, #ede8e0 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  aspect-ratio: 3/4;
`;

export function Products() {
  const { lang } = useLanguage();
  const { ref } = useInView({ threshold: 0.05 });
  const [filter, setFilter] = useState('all');
  const { addToCart } = useCart();
  const { region, symbol: currSym } = useRegion();
  const navigate = useNavigate();
  const isMxn = region === 'mx';
  const currLabel = region === 'mx' ? '' : region === 'ca' ? ' CAD' : ' USD';
  const fmt = (amount) => `${currSym}${Number(amount).toFixed(2)}${currLabel}`;
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then(r => { if (!r.ok) throw new Error('Error al cargar productos'); return r.json(); })
      .then(data => { setProducts(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const visible = filter === 'all' ? products : products.filter(s => s.v === filter);

  return (
    <Section id="productos">
      <Container>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:24, flexWrap:'wrap', gap:16}}>
          <div>
            <p style={{fontFamily:'Raleway', color:'#C07020', margin:0}}>
              {lang==='es' ? 'CAT\u00C1LOGO' : lang==='fr' ? 'CATALOGUE' : 'CATALOG'}
            </p>
          </div>
          <FilterBar>
            {[
              { key:'all',      label: lang==='es' ? 'Todos' : lang==='fr' ? 'Tous' : 'All'  },
              { key:'natural',  label: 'Natural'                       },
              { key:'envinada', label: 'Envinada'                      },
            ].map(opt => (
              <FilterBtn key={opt.key} $active={filter === opt.key} onClick={() => setFilter(opt.key)}>
                {opt.label}
              </FilterBtn>
            ))}
          </FilterBar>
          <div style={{fontSize:'.82rem', color:'rgba(26,18,8,.4)'}}>
            {!loading && `${visible.length} ${lang==='es' ? 'productos' : lang==='fr' ? 'produits' : 'products'}`}
          </div>
        </div>

        <Grid ref={ref}>
          {loading && Array.from({length: 4}).map((_, i) => <SkeletonCard key={i} />)}

          {!loading && error && (
            <div style={{gridColumn:'1/-1', padding:'40px 0', textAlign:'center', color:'rgba(26,18,8,.5)'}}>
              {lang==='es' ? 'No se pudieron cargar los productos.' : lang==='fr' ? 'Impossible de charger les produits.' : 'Could not load products.'}
            </div>
          )}

          {!loading && !error && visible.length === 0 && (
            <div style={{gridColumn:'1/-1', padding:'40px 0', textAlign:'center', color:'rgba(26,18,8,.5)'}}>
              {lang==='es' ? 'Sin productos disponibles.' : lang==='fr' ? 'Aucun produit disponible.' : 'No products available.'}
            </div>
          )}

          {!loading && !error && visible.map(product => {
            const item = product[lang] || product.en;
            return (
              <Card key={product.id} onClick={() => navigate(`/${region}/product/${product.id}`)}>
                <ImageArea>
                  <TypePill>{product.v}</TypePill>
                  {product.image
                    ? <img src={product.image} alt={item?.name} style={{width:'92%',height:'92%',objectFit:'contain'}} />
                    : <div style={{fontSize:36}}>🛙</div>}
                  <QuickBtn onClick={e => { e.stopPropagation(); addToCart(product.id, 1); }} aria-label="Agregar al carrito">
                    <ShoppingBag size={14} />
                  </QuickBtn>
                </ImageArea>
                <CardBody>
                  <ProductName>{item?.name}</ProductName>
                  <ProductDesc>{item?.desc}</ProductDesc>
                  <CardFooter>
                    <div style={{color:'#7C3A00', fontWeight:700}}>
                      {(() => { const p = region==='mx'?(product.priceMxn||product.price):region==='ca'?(product.priceCad||product.price):product.price; return p > 0 ? fmt(p) : (lang==='es'?'Precio a consultar':lang==='fr'?'Prix sur demande':'Price on request'); })()}
                    </div>
                    <div style={{color:'#7C3A00', fontSize:'.8rem', fontWeight:500}}>
                      {lang==='es' ? 'Ver \u2192' : lang==='fr' ? 'Voir \u2192' : 'View \u2192'}
                    </div>
                  </CardFooter>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
}

export default Products;